import RootLayout from '@/layouts/root';
import type { AppProps } from 'next/app';
import Head from 'next/head';
import '@/styles/globals.css';
import 'react-toastify/dist/ReactToastify.css';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import React from 'react';
import { appWithTranslation, useTranslation } from 'next-i18next';
import { useSessionUser } from '@/hooks/useSessionUser';
import Loading from '@/components/Loading/Loading';
import { AudioProvider } from '@/context/AudioContext';
import { useRouter } from 'next/router';
import SettingModal from '@/components/_modals/SettingModal';
import { useSessionUserStore } from '@/hooks/stores/useSessionUserStore';

const App = ({ Component, pageProps }: AppProps) => {
  const router = useRouter();
  const { isFetching, user, loginError, login } = useSessionUser();
  const { modalIds } = useSessionUserStore();
  const { i18n, t } = useTranslation();
  const [displaySettingModal, setDisplaySettingModal] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const idsRef = React.useRef<string[]>([]);
  idsRef.current = modalIds;

  const handleKeydownEsc = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (idsRef.current.length > 0) return;

      setDisplaySettingModal(true);
    }
  };

  const handleCloseSettingModal = () => {
    setDisplaySettingModal(false);
  };

  React.useEffect(() => {
    let region = 'jp';
    const setLanguage = async () => {
      const currentRegion = await window.electronAPI.getCountryCode();
      if (currentRegion !== 'jp') {
        region = 'us';
      }
      i18n.changeLanguage(region);
    };

    const initialLogin = async () => {
      await setLanguage();
      await login();
    };

    initialLogin();

    document.addEventListener('keydown', handleKeydownEsc);
    const handleStart = () => {
      setLoading(true);
    };
    const handleComplete = () => {
      setLoading(false);
    };

    router.events.on('routeChangeStart', handleStart);
    router.events.on('routeChangeComplete', handleComplete);
    router.events.on('routeChangeError', handleComplete);

    return () => {
      document.removeEventListener('keydown', handleKeydownEsc);
      router.events.off('routeChangeStart', handleStart);
      router.events.off('routeChangeComplete', handleComplete);
      router.events.off('routeChangeError', handleComplete);
    };
  }, []);

  return (
    <RootLayout>
      <Head>
        <title>{t('title')}</title>
      </Head>
      <Head>
        <meta name="google-adsense-account" content="ca-pub-4534700259643183" />
      </Head>
      <div className="relative bg-[url('/images/bg.png')] h-full bg-cover bg-center bg-no-repeat">
        {(isFetching || loading) && <Loading size={8} />}
        {user && (
          <AudioProvider>
            <Component {...pageProps} />
            {displaySettingModal && (
              <SettingModal onClose={handleCloseSettingModal} />
            )}
          </AudioProvider>
        )}
        {loginError && (
          <AudioProvider>
            <Component {...pageProps} />
          </AudioProvider>
        )}
      </div>
    </RootLayout>
  );
};

export default appWithTranslation(App);
